import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Alert } from '@/components/notifications/Alert/Alert';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { ROUTE_KEYS } from '@/navigation/constants';
import { FormVersionKind } from '@/types/schema';
import { formatDateToMonDDYYYYWithTime } from '@/utils/formatting/dates';
import { useAdminFormTestResultsQuery } from './AdminFormTests.generated';
interface TestSubmissionRow {
  id: string;
  household: string;
  status: string;
  documents: string;
}
const columns: Column<TestSubmissionRow>[] = [{
  field: 'household',
  headerName: 'Household',
  flex: 1,
  renderCell: TextRenderer()
}, {
  field: 'status',
  headerName: 'Status',
  flex: 1,
  renderCell: TextRenderer()
}, {
  field: 'documents',
  headerName: 'Documents',
  flex: 2,
  renderCell: TextRenderer()
}];
export function AdminFormTestRunPage() {
  const $ = _c(33);
  const formId = useRequiredParam("formId");
  const versionId = useRequiredParam("versionId");
  const {
    navigate
  } = useNavigateToRoute();
  let t0;
  if ($[0] !== versionId) {
    t0 = {
      variables: {
        versionID: versionId
      },
      fetchPolicy: "network-only",
      pollInterval: 5000
    };
    $[0] = versionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const results = useAdminFormTestResultsQuery(t0);
  const version = results.data?.node?.__typename === "FormVersion" ? results.data.node : undefined;
  const invalidVersion = !results.loading && (!version || version.kind !== FormVersionKind.Test || version.form.id !== formId);
  if (invalidVersion) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Alert severity="error">This test run is unavailable for this form.</Alert>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== results.data?.submissions.edges) {
    t1 = results.data?.submissions.edges ?? [];
    $[3] = results.data?.submissions.edges;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = t1.flatMap(_temp2);
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const rows = t2;
  let t3;
  if ($[7] !== version) {
    t3 = version ? `Test run - ${formatDateToMonDDYYYYWithTime(version.createdAt)}` : "Test run";
    $[7] = version;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== formId || $[10] !== navigate) {
    t4 = <Button size="sm" variant="secondary" onClick={() => navigate(ROUTE_KEYS.ADMIN_FORM_TESTS, {
      formId
    })}>Test runs</Button>;
    $[9] = formId;
    $[10] = navigate;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== version) {
    t5 = version && <Typography variant="body-s">Snapshot version {version.version}. Uses an unpublished snapshot of the draft at launch. Each row is one household in this run. Start a new test to test more households or a changed draft.</Typography>;
    $[12] = version;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== results.error) {
    t6 = results.error && <Alert severity="error">Test results could not be loaded.</Alert>;
    $[14] = results.error;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      noRowsOverlay: _temp3
    };
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== formId || $[18] !== navigate || $[19] !== versionId) {
    t8 = params => navigate(ROUTE_KEYS.ADMIN_FORM_TEST_SUBMISSION, {
      formId,
      versionId,
      submissionId: params.row.id
    });
    $[17] = formId;
    $[18] = navigate;
    $[19] = versionId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== results.loading || $[22] !== rows || $[23] !== t8) {
    t9 = <DataTableCard><DataTable columns={columns} rows={rows} loading={results.loading} pagination={false} slots={t7} onRowClick={t8} /></DataTableCard>;
    $[21] = results.loading;
    $[22] = rows;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t5 || $[26] !== t6 || $[27] !== t9) {
    t10 = <Stack spacing={2}>{t5}{t6}{t9}</Stack>;
    $[25] = t5;
    $[26] = t6;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t3 || $[31] !== t4) {
    t11 = <SubpageLayout heading={t3} actions={t4}>{t10}</SubpageLayout>;
    $[29] = t10;
    $[30] = t3;
    $[31] = t4;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp3() {
  return <TableEmptyState text="No launched cases for this snapshot" />;
}
function _temp2(edge) {
  return edge?.node ? [{
    id: edge.node.id,
    household: edge.node.household.name,
    status: edge.node.status,
    documents: (edge.node.sourceDocuments.edges ?? []).flatMap(_temp).join(", ")
  }] : [];
}
function _temp(document) {
  return document?.node?.name ?? [];
}